/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {FC, CanBeSignals, getProp, $Sprite, $SetVisibility, $Container} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store} from '@gongxigames/framework/core/fragments/store/Store';
import {$UIText, UITextStyles} from '@gongxigames/framework/slotV2/ui/ui-koala/JSX/UIText';
import type {SpinBtnMode, SpinBtnCurrentAction} from './SpinBtn';
import {Container as PixiContainer} from 'pixi.js';
import {computedBtnType, computedIcon} from './etc';

export type BtnIconProps = CanBeSignals<{
    /**
     * state of the game
     * base - base game
     * free_spins - free spins
     * autoplay - autoplay is active
     */
    mode: SpinBtnMode;

    /**
     * is Next action is spin or stop(skip)
     */
    currentBtnAction: SpinBtnCurrentAction;

    /**
     * is cursor over button
     */
    focused: boolean;

    /**
     * disable button, click event won't be emitted
     */
    disabled: boolean;

    /**
     * remaining autoplay spins
     */
    autoplayCounter: string;

    /**
     * total free spins collected
     */
    fsTotal: number;

    /**
     * current free spin index
     */
    fsCurrent: number;
}>;

export const $BtnIcon: FC<BtnIconProps> = (props) => {
    const container = new PixiContainer();
    const autoplayCounterStyle = Store.computed<UITextStyles>(() => getProp(props.disabled) ? 'AUTOPLAY_COUNTER_DISABLED': 'AUTOPLAY_COUNTER');
    const freeSpinCounterStyle = Store.computed<UITextStyles>(() => getProp(props.disabled) ? 'FREE_SPIN_COUNTER_DISABLED': 'FREE_SPIN_COUNTER');
    const freeSpinCounterValues = Store.computed(() => {
        return {
            of: getProp(props.fsTotal),
            spin: getProp(props.fsCurrent),
        };
    });

    const btnType = computedBtnType(props.mode, props.currentBtnAction);
    const iconSelect = computedIcon(props.focused, props.disabled, btnType);
    const showAutoplayCounter = Store.computed(() => getProp(props.mode) === 'autoplay');
    const showFreeSpinsCounter = Store.computed(() => getProp(props.mode) === 'free_spins');

    return <$Container name='spin_animation' instance={container}>
        <$SetVisibility selector={iconSelect}>
            <$Sprite name='normal_turbospin' anchor={0.5} texture='spin_arrows_btn_disabled_sys_ui'/>
            <$Sprite name='focused_turbospin' anchor={0.5} texture='spin_arrows_btn_disabled_sys_ui'/>
            <$Sprite name='pressed_turbospin' anchor={0.5} texture='spin_arrows_btn_disabled_sys_ui'/>
            <$Sprite name='disabled_turbospin' anchor={0.5} texture='spin_arrows_btn_disabled_sys_ui'/>

            <$Sprite name='normal_spin' anchor={0.5} texture='spin_arrows_btn_normal_sys_ui'/>
            <$Sprite name='focused_spin' anchor={0.5} texture='spin_arrows_btn_focused_sys_ui'/>
            <$Sprite name='pressed_spin' anchor={0.5} texture='spin_arrows_btn_pressed_sys_ui'/>
            <$Sprite name='disabled_spin' anchor={0.5} texture='spin_arrows_btn_disabled_sys_ui'/>

            <$Sprite name='normal_stop' anchor={0.5} texture='stop_spin_btn_normal_sys_ui'/>
            <$Sprite name='focused_stop' anchor={0.5} texture='stop_spin_btn_focused_sys_ui'/>
            <$Sprite name='pressed_stop' anchor={0.5} texture='stop_spin_btn_pressed_sys_ui'/>
            <$Sprite name='disabled_stop' anchor={0.5} texture='stop_spin_btn_disabled_sys_ui'/>

            <$Sprite name='normal_autospin' anchor={0.5} texture='autospin_btn_normal_sys_ui'/>
            <$Sprite name='focused_autospin' anchor={0.5} texture='autospin_btn_focused_sys_ui'/>
            <$Sprite name='pressed_autospin' anchor={0.5} texture='autospin_btn_pressed_sys_ui'/>
            <$Sprite name='disabled_autospin' anchor={0.5} texture='autospin_btn_disabled_sys_ui'/>
        </$SetVisibility>

        <$UIText
            visible={showAutoplayCounter}
            text={props.autoplayCounter}
            name='autoplay_counter'
            stylePreset={autoplayCounterStyle}
            maxWidth={100}
        />
        <$UIText
            visible={showFreeSpinsCounter}
            text={'{{spin}}/{{of}}'}
            values={freeSpinCounterValues}
            name='free_spin_counter'
            stylePreset={freeSpinCounterStyle}
            maxWidth={200}
        />
    </$Container>;
};